import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X as XIcon } from 'lucide-react'
import { cn } from './cn'
import { Button } from './button'

// Keep in sync with the `modal-out` / `scrim-out` keyframe durations in
// src/index.css. Drift = either a mid-animation flash-remove (fires early)
// or a lingering ghost modal after close (fires late).
const CLOSE_ANIMATION_MS = 160

export type ModalProps = {
  title: ReactNode
  onClose: () => void
  children: ReactNode
  foot?: ReactNode
  width?: number
  /** Render wider modal at `lg` (720) / `xl` (920). Overrides `width` if set. */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  className?: string
  /** Set to false to hide the default close button (rare). */
  showCloseButton?: boolean
  /**
   * When true, Esc / scrim click / close-button trigger a small inline confirm
   * "Discard changes?" before firing onClose. Pass `formState.isDirty` from
   * react-hook-form (or any boolean) so the guard activates only when the user
   * has unsaved edits. Programmatic `onClose()` calls are unaffected — only
   * user-initiated close attempts trigger the prompt.
   */
  dirty?: boolean
}

const SIZE_WIDTHS: Record<NonNullable<ModalProps['size']>, number> = {
  xs: 360,
  sm: 440,
  md: 560,
  lg: 720,
  xl: 920,
}

export function Modal({
  title,
  onClose,
  children,
  foot,
  width,
  size,
  className,
  showCloseButton = true,
  dirty = false,
}: ModalProps) {
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const [showDiscardPrompt, setShowDiscardPrompt] = useState(false)

  // Two-phase close so the exit animation can play before the component
  // unmounts. `isClosing` flips first → CSS plays the out keyframes →
  // after the duration we invoke the parent's `onClose`, which actually
  // removes the modal from the tree. Mirrors the Drawer primitive.
  const [isClosing, setIsClosing] = useState(false)
  const closeTimerRef = useRef<number | null>(null)
  const startClose = useCallback(() => {
    if (isClosing) return
    setIsClosing(true)
    closeTimerRef.current = window.setTimeout(() => {
      onClose()
    }, CLOSE_ANIMATION_MS)
  }, [isClosing, onClose])

  /** User-initiated close: gated by the dirty guard when caller opted in. */
  const requestClose = useCallback(() => {
    if (dirty) {
      setShowDiscardPrompt(true)
      return
    }
    startClose()
  }, [dirty, startClose])

  useEffect(() => {
    return () => {
      if (closeTimerRef.current != null) {
        window.clearTimeout(closeTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // While the inline discard prompt is up, Escape dismisses *it*, not the modal.
        if (showDiscardPrompt) setShowDiscardPrompt(false)
        else requestClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [requestClose, showDiscardPrompt])

  // Lock body scroll while open.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [])

  // Save focus on mount + restore on unmount; auto-focus the first focusable
  // element inside the modal so keyboard users land in the dialog instead of
  // wherever they were before, and return there on close (WCAG 2.4.3).
  useEffect(() => {
    const previousActive = document.activeElement as HTMLElement | null
    const el = panelRef.current
    if (el) {
      const target = el.querySelector<HTMLElement>('[data-autofocus], button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      target?.focus()
    }
    return () => {
      if (previousActive && document.body.contains(previousActive)) {
        previousActive.focus()
      }
    }
  }, [])

  const effectiveWidth = size ? SIZE_WIDTHS[size] : (width ?? 560)

  // Portal to document.body so the modal escapes any ancestor that
  // establishes a containing block for `position: fixed` — most notably
  // the topbar's `backdrop-filter`. Without the portal, dialogs opened
  // from the topbar (FeedbackDialog, the TutorialTrigger pill, etc.)
  // collapse to a thin clipped strip across the top of the viewport.
  // Same fix applied to <Drawer>.
  const tree = (
    <div
      className={cn('modal-scrim', isClosing && 'modal-scrim--closing')}
      onMouseDown={requestClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div
        className={cn('modal', isClosing && 'modal--closing', className)}
        style={{ width: effectiveWidth }}
        onMouseDown={(e) => e.stopPropagation()}
        ref={panelRef}
        tabIndex={-1}
      >
        <div className="modal-head">
          <h2 id={titleId}>{title}</h2>
          {showCloseButton && (
            <button type="button" className="btn ghost sm" onClick={requestClose} aria-label="Cerrar">
              <XIcon size={12} />
            </button>
          )}
        </div>
        <div className="modal-body">{children}</div>
        {foot && <div className="modal-foot">{foot}</div>}
        {showDiscardPrompt && (
          <DiscardPrompt
            onCancel={() => setShowDiscardPrompt(false)}
            onDiscard={() => {
              setShowDiscardPrompt(false)
              startClose()
            }}
          />
        )}
      </div>
    </div>
  )
  if (typeof document === 'undefined') return tree
  return createPortal(tree, document.body)
}

/** Inline confirm rendered on top of a Modal/Drawer when the user tries to
 * close while `dirty=true`. Kept private — the Modal/Drawer manages its
 * lifecycle. Visual: a centered dialog inside the parent panel with a
 * dark scrim behind it (was a bottom-pinned strip — too easy to miss
 * under the action bar). Click on the scrim cancels (standard modal
 * behavior). The `data-discard-prompt` hook on the scrim lets E2E +
 * unit tests target it without selecting on copy. */
export function DiscardPrompt({
  onCancel,
  onDiscard,
  message = '¿Descartar los cambios sin guardar?',
  discardLabel = 'Descartar',
  cancelLabel = 'Seguir editando',
}: {
  onCancel: () => void
  onDiscard: () => void
  message?: ReactNode
  discardLabel?: string
  cancelLabel?: string
}) {
  return (
    <div
      className="discard-prompt"
      data-discard-prompt
      onClick={(e) => {
        // Cancel on backdrop click; ignore clicks bubbling from the dialog.
        if (e.target === e.currentTarget) onCancel()
      }}
    >
      <div
        className="discard-prompt__dialog"
        role="alertdialog"
        aria-modal="true"
        aria-label={typeof message === 'string' ? message : 'Confirmar descarte'}
      >
        <div className="discard-prompt__msg">{message}</div>
        <div className="discard-prompt__actions">
          <Button variant="ghost" onClick={onCancel} data-autofocus>
            {cancelLabel}
          </Button>
          <Button variant="danger" onClick={onDiscard}>
            {discardLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}
